/**
 * TagFormModal — create or edit a tag: name, color, and "auto-remove" tags.
 * Purely presentational: TagManager owns the state and the save handlers.
 *
 * Auto-remove (excludesTags): when THIS tag is added to a customer, the listed tags are
 * removed from that customer automatically (server side, tag management service).
 *
 * Props:
 *   open, title, okText
 *   value      { name, color, excludesTags: [tagId] }
 *   onChange   fn(partialValue)
 *   onOk / onCancel
 *   allTags    tags from /api/tags/stats (for the auto-remove picker)
 *   selfId     id of the tag being edited (excluded from the picker), or null when creating
 *
 * Colors: the preset swatches + a last "custom" swatch that opens a color picker. The server
 * stores any string; we always save "#RRGGBB" (no alpha) because softColor() in ui/Badges.js
 * tints badges from that format.
 */
import React from 'react';
import { BgColorsOutlined } from '@ant-design/icons';
import { Modal, Input, Select, Field, FormGrid, ColorPicker, Tooltip } from '../../ui';
import { PRESET_COLORS } from './tagColors';
import classes from './TagManager.css';

const TagFormModal = ({ open, title, okText, value, onChange, onOk, onCancel, allTags, selfId }) => {
  if (!value) return null;
  const excludes = value.excludesTags || [];
  const isCustom = !!value.color && !PRESET_COLORS.includes(value.color);
  const options = allTags
    .filter((t) => t.id !== selfId)
    .map((t) => ({
      value: t.id,
      label: t.name,
      color: t.color,
    }));

  return (
    <Modal open={open} title={title} okText={okText} cancelText="ביטול" onOk={onOk} onCancel={onCancel} destroyOnHidden>
      <FormGrid>
        <Field label="שם הטאג" required full>
          <Input autoFocus value={value.name} onChange={(e) => onChange({ name: e.target.value })} onPressEnter={onOk} placeholder="שם הטאג…" />
        </Field>

        <Field label="צבע" full>
          <div className={classes.swatches} role="radiogroup" aria-label="צבע">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                role="radio"
                aria-checked={value.color === color}
                aria-label={color}
                className={classes.swatch}
                data-selected={value.color === color}
                style={{ background: color }}
                onClick={() => onChange({ color })}
              />
            ))}
            {/* Custom color: shows the tag's own color when it isn't one of the presets */}
            <ColorPicker
              disabledAlpha
              disabledFormat
              format="hex"
              value={value.color || PRESET_COLORS[0]}
              onChangeComplete={(c) => onChange({ color: c.toHexString().slice(0, 7).toUpperCase() })}
            >
              <Tooltip title="צבע אחר…">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isCustom}
                  aria-label="צבע אחר"
                  className={`${classes.swatch} ${isCustom ? '' : classes.swatchCustom}`}
                  data-selected={isCustom}
                  style={isCustom ? { background: value.color } : undefined}
                >
                  {isCustom ? null : <BgColorsOutlined />}
                </button>
              </Tooltip>
            </ColorPicker>
          </div>
        </Field>

        <Field
          label="טאגים להסרה אוטומטית"
          hint={
            excludes.length > 0
              ? `כשטאג${value.name ? ` "${value.name}"` : ' זה'} יתווסף ללקוח, ${excludes.length} טאג/ים יוסרו ממנו אוטומטית`
              : 'כשטאג זה יתווסף ללקוח, הטאגים שתבחרו כאן יוסרו ממנו אוטומטית'
          }
          full
        >
          <Select
            mode="multiple"
            allowClear
            optionFilterProp="label"
            placeholder="חפש טאג…"
            value={excludes}
            onChange={(ids) => onChange({ excludesTags: ids })}
            options={options}
            optionRender={(opt) => (
              <span className={classes.optionWithDot}>
                <span className={classes.dot} style={{ background: opt.data.color }} />
                {opt.label}
              </span>
            )}
          />
        </Field>
      </FormGrid>
    </Modal>
  );
};

export default TagFormModal;
